import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Lock, CheckCircle, AlertCircle, Clock, ArrowLeft, ChevronRight, Award } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const GATE_META = {
  T1: { label: 'Baseline Assessment', desc: 'Establishes your starting point — no unlock required.', color: 'from-gray-600 to-gray-700', unlocks: 'Stage 1' },
  T2: { label: 'Stage 1 Gate', desc: 'Unlock Stage 2 — Capability. Pass ≥70%.', color: 'from-violet-600 to-purple-700', unlocks: 'Stage 2 — Capability' },
  T3: { label: 'Stage 2 Gate', desc: 'Unlock Stage 3 — Leadership. Pass ≥70%.', color: 'from-blue-600 to-cyan-600', unlocks: 'Stage 3 — Leadership' },
  T4: { label: 'Final Gate', desc: 'Unlock your Skills Passport & SRI Score. Pass ≥70%.', color: 'from-amber-500 to-orange-600', unlocks: 'Skills Passport' }
};

const WEIGHT_CONFIG = [
  { type: 'MCQ', label: 'Multiple Choice', weight: 15, color: 'bg-blue-500', desc: '15% of total score' },
  { type: 'FIB', label: 'Fill in the Blank', weight: 10, color: 'bg-emerald-500', desc: '10% of total score' },
  { type: 'SJT', label: 'Situational Judgment', weight: 75, color: 'bg-violet-600', desc: '75% of total score — the core of SMAART' }
];

const StageGate = () => {
  const { gateCode } = useParams();
  const navigate = useNavigate();
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [phase, setPhase] = useState('intro'); // intro | mcq | fib | sjt | result
  const [currentQ, setCurrentQ] = useState(0);
  const [mcqAnswers, setMcqAnswers] = useState([]);
  const [fibAnswers, setFibAnswers] = useState([]);
  const [sjtAnswers, setSjtAnswers] = useState([]);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);

  useEffect(() => { fetchGate(); }, [gateCode]);

  // Timer
  useEffect(() => {
    if (phase === 'mcq' || phase === 'fib' || phase === 'sjt') {
      if (timeLeft === null && assessment) {
        const sec = (assessment.duration || 1440) * 60;
        setTimeLeft(sec);
      }
    }
  }, [phase, assessment]);

  useEffect(() => {
    if (timeLeft === null || timeLeft <= 0) return;
    const t = setTimeout(() => setTimeLeft(tl => tl - 1), 1000);
    return () => clearTimeout(t);
  }, [timeLeft]);

  const fetchGate = async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/assessments?tier=${gateCode}&status=active&limit=1`);
      const list = data.data || data.assessments || [];
      if (list.length) setAssessment(list[0]);
    } catch (e) {
      toast.error('Failed to load gate assessment.');
    } finally { setLoading(false); }
  };

  const submitAssessment = async () => {
    try {
      setSubmitting(true);
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const { data } = await api.post('/programme/submit-gate-assessment', {
        studentId: user._id || user.id,
        assessmentId: assessment._id,
        gateCode,
        mcqAnswers,
        fibAnswers,
        sjtAnswers
      });
      setResult(data.data);
      setPhase('result');
    } catch (e) {
      toast.error('Submission failed. Try again.');
    } finally { setSubmitting(false); }
  };

  const meta = GATE_META[gateCode] || GATE_META.T1;
  const formatTime = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-violet-600" />
    </div>
  );

  // ── INTRO ──────────────────────────────────────────────────────────────────
  if (phase === 'intro') return (
    <div className="max-w-2xl mx-auto py-8 space-y-6">
      <button onClick={() => navigate('/student/my-courses')} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700">
        <ArrowLeft className="w-4 h-4" /> Back to My Courses
      </button>

      <div className={`bg-gradient-to-br ${meta.color} rounded-2xl p-8 text-white shadow-xl`}>
        <div className="text-sm font-bold opacity-70 mb-1">{gateCode} — Gate Assessment</div>
        <h1 className="text-3xl font-black mb-2">{meta.label}</h1>
        <p className="opacity-80">{meta.desc}</p>
        <div className="mt-4 inline-flex items-center gap-2 bg-white/20 px-3 py-1.5 rounded-full text-sm font-bold">
          <Award className="w-4 h-4" /> Unlocks: {meta.unlocks}
        </div>
      </div>

      {/* Score weights */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
        <h3 className="font-bold text-gray-900 mb-4">Assessment Structure</h3>
        <div className="space-y-3">
          {WEIGHT_CONFIG.map(w => (
            <div key={w.type} className="flex items-center gap-4">
              <div className={`w-14 h-8 ${w.color} rounded-lg flex items-center justify-center text-white text-xs font-black`}>{w.weight}%</div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-semibold text-gray-800">{w.label} ({w.type})</span>
                  <span className="text-xs text-gray-400">{w.desc}</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full">
                  <div className={`h-full ${w.color} rounded-full`} style={{ width: `${w.weight}%` }} />
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 p-3 bg-violet-50 rounded-xl border border-violet-100 text-sm text-violet-700">
          <strong>Passing score: ≥70% weighted total.</strong> You have up to 3 attempts.
        </div>
      </div>

      {!assessment ? (
        <div className="text-center py-8 bg-amber-50 border border-amber-200 rounded-2xl">
          <AlertCircle className="w-10 h-10 text-amber-500 mx-auto mb-2" />
          <p className="font-semibold text-amber-800">Gate assessment not published yet.</p>
          <p className="text-sm text-amber-600">Contact your coordinator.</p>
        </div>
      ) : (
        <button onClick={() => setPhase('mcq')}
          className={`w-full py-4 bg-gradient-to-r ${meta.color} text-white rounded-2xl font-bold text-lg shadow-xl hover:shadow-2xl hover:-translate-y-0.5 transition-all`}>
          Start Gate Assessment →
        </button>
      )}
    </div>
  );

  // ── MCQ Phase ─────────────────────────────────────────────────────────────
  if (phase === 'mcq') {
    const questions = assessment?.questions || [];
    const q = questions[currentQ];
    if (!q) { setPhase('fib'); return null; }
    const answered = mcqAnswers.find(a => a.questionId === q._id);
    return (
      <div className="max-w-2xl mx-auto py-6 space-y-5">
        <div className="flex items-center justify-between">
          <span className="text-sm font-bold text-blue-600 bg-blue-50 px-3 py-1 rounded-full">MCQ · 15% weight</span>
          {timeLeft !== null && <span className="flex items-center gap-1 text-sm text-gray-500"><Clock className="w-4 h-4" />{formatTime(timeLeft)}</span>}
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <p className="text-xs text-gray-400 mb-2">Question {currentQ + 1} of {questions.length}</p>
          <p className="text-lg font-semibold text-gray-900 mb-5">{q.questionText}</p>
          <div className="space-y-3">
            {(q.options || []).map(opt => (
              <button key={opt.value}
                onClick={() => { const others = mcqAnswers.filter(a => a.questionId !== q._id); setMcqAnswers([...others, { questionId: q._id, selectedValue: opt.value }]); }}
                className={`w-full text-left px-4 py-3 rounded-xl border-2 font-medium transition-all text-sm
                  ${answered?.selectedValue === opt.value ? 'border-blue-500 bg-blue-50 text-blue-800' : 'border-gray-200 hover:border-blue-300 text-gray-700'}`}
              >
                <span className="font-bold mr-2 text-gray-400">{opt.label || opt.value}.</span>{opt.label ? opt.value : opt.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex gap-3">
          {currentQ > 0 && <button onClick={() => setCurrentQ(q => q - 1)} className="px-5 py-3 border border-gray-200 rounded-xl text-gray-600 font-medium hover:bg-gray-50">← Back</button>}
          <button onClick={() => currentQ + 1 < questions.length ? setCurrentQ(q => q + 1) : setPhase('fib')}
            className="flex-1 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-colors">
            {currentQ + 1 < questions.length ? 'Next →' : 'Continue to FIB →'}
          </button>
        </div>
        <div className="flex gap-1">{questions.map((_, i) => <div key={i} className={`h-1.5 flex-1 rounded-full ${mcqAnswers.find(a => a.questionId === questions[i]._id) ? 'bg-blue-500' : 'bg-gray-200'}`} />)}</div>
      </div>
    );
  }

  // ── FIB Phase ─────────────────────────────────────────────────────────────
  if (phase === 'fib') {
    const questions = assessment?.fibQuestions || [];
    if (!questions.length) { setPhase('sjt'); return null; }
    const q = questions[currentQ];
    if (!q) { setPhase('sjt'); return null; }
    return (
      <div className="max-w-2xl mx-auto py-6 space-y-5">
        <span className="text-sm font-bold text-emerald-600 bg-emerald-50 px-3 py-1 rounded-full">Fill in the Blank · 10% weight</span>
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <p className="text-xs text-gray-400 mb-2">Question {currentQ + 1} of {questions.length}</p>
          <p className="text-lg font-semibold text-gray-900 mb-5">{q.questionText?.replace('___', '________')}</p>
          {q.hint && <p className="text-xs text-gray-400 mb-3 italic">Hint: {q.hint}</p>}
          <input
            className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 text-gray-900 font-medium focus:outline-none focus:border-emerald-400 transition-colors"
            placeholder="Type your answer…"
            value={fibAnswers.find(a => a.questionId === q._id)?.answer || ''}
            onChange={e => { const o = fibAnswers.filter(a => a.questionId !== q._id); setFibAnswers([...o, { questionId: q._id, answer: e.target.value }]); }}
          />
        </div>
        <div className="flex gap-3">
          {currentQ > 0 && <button onClick={() => setCurrentQ(q => q - 1)} className="px-5 py-3 border border-gray-200 rounded-xl text-gray-600 font-medium hover:bg-gray-50">← Back</button>}
          <button onClick={() => { setCurrentQ(0); currentQ + 1 < questions.length ? setCurrentQ(q => q + 1) : setPhase('sjt'); }}
            className="flex-1 py-3 bg-emerald-600 text-white rounded-xl font-bold hover:bg-emerald-700 transition-colors">
            {currentQ + 1 < questions.length ? 'Next →' : 'Continue to SJT →'}
          </button>
        </div>
      </div>
    );
  }

  // ── SJT Phase ─────────────────────────────────────────────────────────────
  if (phase === 'sjt') {
    const questions = assessment?.sjtQuestions || [];
    const q = questions[currentQ];
    if (!q) {
      return (
        <div className="max-w-2xl mx-auto py-8 text-center space-y-4">
          <p className="text-gray-500">All SJT questions answered.</p>
          <button onClick={submitAssessment} disabled={submitting}
            className="px-8 py-4 bg-gradient-to-r from-violet-600 to-purple-700 text-white rounded-2xl font-bold text-lg shadow-xl disabled:opacity-60">
            {submitting ? 'Submitting…' : 'Submit Assessment →'}
          </button>
        </div>
      );
    }
    const answered = sjtAnswers.find(a => a.questionId === q._id);
    return (
      <div className="max-w-2xl mx-auto py-6 space-y-5">
        <div className="flex items-center justify-between">
          <span className="text-sm font-bold text-violet-600 bg-violet-50 px-3 py-1 rounded-full">Situational Judgment · 75% weight</span>
          <span className="text-xs text-gray-400">{currentQ + 1}/{questions.length}</span>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="bg-violet-50 border border-violet-100 rounded-xl p-4 mb-4 text-sm text-violet-900 font-medium leading-relaxed">{q.scenario}</div>
          <p className="text-base font-semibold text-gray-900 mb-4">{q.question}</p>
          <div className="space-y-3">
            {(q.options || []).map(opt => (
              <button key={opt.label}
                onClick={() => { const o = sjtAnswers.filter(a => a.questionId !== q._id); setSjtAnswers([...o, { questionId: q._id, selectedLabel: opt.label }]); }}
                className={`w-full text-left px-4 py-3 rounded-xl border-2 font-medium transition-all text-sm
                  ${answered?.selectedLabel === opt.label ? 'border-violet-500 bg-violet-50 text-violet-800' : 'border-gray-200 hover:border-violet-300 text-gray-700'}`}
              >
                <span className="font-black mr-2">{opt.label}.</span>{opt.text}
              </button>
            ))}
          </div>
        </div>
        <div className="flex gap-3">
          {currentQ > 0 && <button onClick={() => setCurrentQ(q => q - 1)} className="px-5 py-3 border border-gray-200 rounded-xl text-gray-600 font-medium">← Back</button>}
          <button onClick={() => { if (currentQ + 1 < questions.length) { setCurrentQ(q => q + 1); } else { submitAssessment(); } }}
            disabled={!answered || submitting}
            className="flex-1 py-3 bg-violet-600 text-white rounded-xl font-bold hover:bg-violet-700 disabled:opacity-50 transition-colors">
            {submitting ? 'Submitting…' : currentQ + 1 < questions.length ? 'Next →' : 'Submit Assessment →'}
          </button>
        </div>
        <div className="flex gap-1">{questions.map((_, i) => <div key={i} className={`h-1.5 flex-1 rounded-full ${sjtAnswers.find(a => a.questionId === questions[i]._id) ? 'bg-violet-500' : 'bg-gray-200'}`} />)}</div>
      </div>
    );
  }

  // ── RESULT ────────────────────────────────────────────────────────────────
  if (phase === 'result' && result) {
    const passed = result.passed;
    const bd = result.breakdown || {};
    return (
      <div className="max-w-2xl mx-auto py-8 space-y-6">
        <div className={`rounded-2xl p-8 text-white shadow-xl text-center ${passed ? 'bg-gradient-to-br from-green-500 to-emerald-600' : 'bg-gradient-to-br from-red-500 to-rose-600'}`}>
          {passed ? <CheckCircle className="w-14 h-14 mx-auto mb-3" /> : <AlertCircle className="w-14 h-14 mx-auto mb-3" />}
          <h2 className="text-3xl font-black">{passed ? '🎉 Gate Passed!' : 'Not Passed Yet'}</h2>
          <p className="text-4xl font-black mt-2">{result.totalWeightedScore}%</p>
          <p className="opacity-80 mt-1">{passed ? `${meta.unlocks} is now unlocked!` : `Need ≥70% to unlock ${meta.unlocks}`}</p>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-4">
          <h3 className="font-bold text-gray-900">Score Breakdown</h3>
          {[['MCQ', bd.mcq, 'bg-blue-500'], ['FIB', bd.fib, 'bg-emerald-500'], ['SJT', bd.sjt, 'bg-violet-600']].map(([type, d, cls]) => d && (
            <div key={type} className="flex items-center gap-4">
              <div className={`w-12 h-8 ${cls} rounded-lg flex items-center justify-center text-white text-xs font-black`}>{d.weight}%</div>
              <div className="flex-1">
                <div className="flex justify-between text-sm mb-1"><span className="font-medium text-gray-800">{type}</span><span className="text-gray-500">{d.weighted || d.pct}% weighted</span></div>
                <div className="h-2 bg-gray-100 rounded-full"><div className={`h-full ${cls} rounded-full`} style={{ width: `${d.weighted || d.pct || 0}%` }} /></div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex gap-3">
          {!passed && result.attemptsRemaining > 0 && (
            <button onClick={() => { setPhase('intro'); setCurrentQ(0); setMcqAnswers([]); setFibAnswers([]); setSjtAnswers([]); setResult(null); }}
              className="flex-1 py-3 border-2 border-violet-500 text-violet-600 rounded-xl font-bold hover:bg-violet-50">
              Retry ({result.attemptsRemaining} left)
            </button>
          )}
          <button onClick={() => navigate('/student/my-courses')}
            className="flex-1 py-3 bg-gray-900 text-white rounded-xl font-bold hover:bg-gray-800">
            {passed ? 'Continue Journey →' : 'Back to My Courses'}
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default StageGate;
